import { useEffect, useRef, useState } from "react";

import { TextArea } from "@blueprintjs/core";
import type { TextAreaProps } from "@blueprintjs/core/lib/esm/components/forms/textArea";

interface ControlledTextareaProps
  extends Omit<TextAreaProps, "value" | "onChange"> {
  value: string;
  onChange: (
    value: string,
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => void;
}

export function ControlledTextarea({
  value: initialValue,
  onChange,
  ...props
}: ControlledTextareaProps) {
  const [value, setValue] = useState(initialValue);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const cursorPositionRef = useRef<number | null>(null);

  useEffect(() => {
    if (textAreaRef.current && cursorPositionRef.current !== null) {
      textAreaRef.current.setSelectionRange(
        cursorPositionRef.current,
        cursorPositionRef.current,
      );
    }
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    const cursorPosition = event.target.selectionStart;

    setValue(newValue);
    cursorPositionRef.current = cursorPosition;

    if (onChange) {
      onChange(newValue, event);
    }
  };

  return (
    <TextArea
      inputRef={(ref) => {
        textAreaRef.current = ref;
      }}
      value={value}
      onChange={handleChange}
      {...props}
    />
  );
}
